import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * UK-formatted date picker.
 * - `value`  is always an ISO string ("yyyy-MM-dd") or "" — safe to send to the API.
 * - The button displays it as dd/MM/yyyy which is what UK users expect.
 * - Optional clear (X) — shown when clearable=true and a value is set.
 */
export function DatePickerUK({ value, onChange, placeholder = "dd/mm/yyyy", clearable = true, className = "", testId }) {
  const [open, setOpen] = useState(false);

  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const display = parsed && isValid(parsed) ? format(parsed, "dd/MM/yyyy") : "";

  const handleSelect = (day) => {
    if (!day) return;
    onChange(format(day, "yyyy-MM-dd"));
    setOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className={cn(
              "flex items-center gap-2 w-full text-left bg-transparent border border-border rounded px-3 py-2 text-sm h-10 transition-colors hover:border-[#83B68B]/60",
              !display && "opacity-60",
            )}
          >
            <CalendarIcon className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
            <span className="flex-1 truncate">{display || placeholder}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={parsed || undefined}
            onSelect={handleSelect}
            weekStartsOn={1}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {clearable && display && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(""); }}
          data-testid={testId ? `${testId}-clear` : undefined}
          aria-label="Clear date"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/10 opacity-60 hover:opacity-100"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
